import React, { useEffect, useRef, useState } from "react";
import {
  BuildDropdownContainer,
  DropdownContainer,
  AddItemContainer,
  ItemContainer,
  TextFilterContainer,
} from "./index.styles";
import UI from "@mile-meeting/mile-ui";
import { debounce } from "lodash";
import EditDropdownItem from "./edit-dropdown-item";
import { useToast } from "hook/useToast";

type BuildDropdownProps = {
  onDropdownClick?: () => void;
  onChange?: (id: string, name: string) => void;
  selected?: string;
  data?: { name: string; id: string; provider?: "GOOGLE" | "MILE" }[];
  onAddOption?: (value: string) => void;
  type?: "building" | "floor";
  open: boolean;
  setOpen: (value: boolean) => void;
  onUpdateBuildInfo?: (type: "building" | "floor", updateId: string) => void;
};

const BuildDropdown = (props: BuildDropdownProps) => {
  const { onChange, selected, data, onAddOption, type, open, setOpen, onUpdateBuildInfo } = props;

  const [search, setSearch] = useState<string>("");
  const [selectedItem, setSelectedItem] = useState<{ name: string; id: string; provider?: "MILE" | "GOOGLE" }>();
  const [dropdownPosition, setDropdownPosition] = useState({ top: 0, left: 0 });
  const ref = useRef<HTMLDivElement>(null);

  const handleItemClick = (
    item: { name: string; id: string; provider?: "MILE" | "GOOGLE" },
    event: React.MouseEvent
  ) => {
    const parentRect = ref.current?.getBoundingClientRect(); // 부모 컨테이너의 좌표 가져오기
    const rect = event.currentTarget.getBoundingClientRect();

    setDropdownPosition({
      top: rect.top - (parentRect?.top || 0) + window.scrollY + 20, // 부모 기준 상대 위치 + 스크롤 보정
      left: rect.left - (parentRect?.left || 0) + window.scrollX, // 부모 기준 상대 위치 + 스크롤 보정
    });
    setSelectedItem(item);
  };

  useEffect(() => {
    const handler = (event: any) => {
      if (open && ref.current && !ref.current.contains(event.target)) {
        setOpen?.(false);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => {
      document.removeEventListener("mousedown", handler);
    };
  }, [props.open]);

  return (
    <BuildDropdownContainer ref={ref} style={{ display: open ? "flex" : "none", width: "100%" }}>
      <TextFilterContainer>
        <UI.Input
          maxLength={50}
          size="small"
          placeholder={`${type === "building" ? "건물명" : "층"} 검색 및 추가`}
          onChange={debounce((value) => {
            if (type === "building") {
              value.length <= 100 && setSearch(value);
            } else if (type === "floor") {
              value.length <= 50 && setSearch(value);
            }
          }, 300)}
        />
      </TextFilterContainer>
      <DropdownContainer>
        {data
          ?.filter((item) => item.name.includes(search))
          .map((item, index) => {
            return (
              <DropdownItem
                isGoogleResource={item.provider === "GOOGLE"}
                key={index}
                name={item.name}
                onClickMenu={(event) => {
                  handleItemClick(item, event);
                }}
                onClickItem={() => onChange?.(item.id, item.name)}
                selected={item.name === selected}
              />
            );
          })}

        {data?.filter((item) => item.name.includes(search)).length === 0 && search && (
          <AddItemContainer onClick={() => onAddOption?.(search)}>
            <UI.Icon name="plus" size={16} color="var(--alias-color-label-alternative)" />
            <UI.Typography type="label01" fontStyle="regular" color="var(--alias-color-label-alternative)">
              '{search}' {type === "building" ? "건물" : ""} 추가
            </UI.Typography>
          </AddItemContainer>
        )}
      </DropdownContainer>
      {selectedItem?.id && (
        <EditDropdownItem
          onUpdate={(updateId) => {
            if (selectedItem.provider) {
              onUpdateBuildInfo?.("building", updateId);
            } else onUpdateBuildInfo?.("floor", updateId);
          }}
          data={selectedItem}
          setData={setSelectedItem}
          position={dropdownPosition}
        />
      )}
    </BuildDropdownContainer>
  );
};

export default BuildDropdown;

const DropdownItem = ({
  name,
  onClickMenu,
  onClickItem,
  selected,
  isGoogleResource = false,
}: {
  onClickMenu?: (e: React.MouseEvent) => void;
  name: string;
  onClickItem?: () => void;
  selected?: boolean;
  isGoogleResource?: boolean;
}) => {
  const { showToast } = useToast();
  return (
    <ItemContainer onClick={onClickItem}>
      <UI.Typography
        type="label01"
        fontStyle={selected ? "bold" : "regular"}
        color="var(--alias-color-label-normal)"
        style={{
          overflow: "hidden",
          textOverflow: "ellipsis",
        }}
      >
        {name}
      </UI.Typography>
      {isGoogleResource && <UI.Icon name="google" size={16} />}
      <div
        className="menu-icon"
        onClick={(e) => {
          e.stopPropagation();
          if (isGoogleResource) {
            showToast("구글 연동된 건물, 층은 층 추가, 정보 수정이 불가합니다.", "warning");
            return;
          }
          onClickMenu?.(e);
        }}
      >
        <UI.IconButton.Normal icon="dotHorizontal" size={16} color="var(--alias-color-label-assistive)" />
      </div>
    </ItemContainer>
  );
};
